import { SetDetail } from 'store/services/trade/tradeApi';
import { MTGCard } from 'types/interfaces/mtgCard';

interface FinishAvailabilityResult {
  availableInFoil: boolean;
  availableInNonFoil: boolean;
  defaultFoil: boolean;
}

/**
 * Determines foil/non-foil availability and the default foil selection for a card.
 *
 * Fallback precedence (highest wins):
 *   1. SetDetail fields — backend-computed from the card's finishes array. Always trusted
 *      even if price data seems to contradict (an unpriced card ≠ a card that doesn't exist
 *      in that finish).
 *   2. Price data — used only when SetDetail fields are undefined. If any price field is
 *      non-null, a non-null price > 0 means that finish exists.
 *   3. card.foil flag / safe default — last resort. For non-foil availability we default to
 *      true (preserves safe fallback when we have zero data).
 */
export function getFinishAvailability(
  matchingSetDetail: SetDetail | undefined,
  card: MTGCard | undefined,
  acceptFoil: boolean,
  acceptNonFoil: boolean,
): FinishAvailabilityResult {
  // Extract price data from card
  const priceData = Array.isArray(card?.price) && card!.price.length > 0
    ? card!.price[0]
    : (card?.price as any);

  const hasPriceData = priceData && (
    priceData.usd != null || priceData.usdFoil != null || priceData.usdEtched != null
  );

  // Determine physical availability using the 3-level fallback chain
  const availableInFoil = matchingSetDetail?.availableInFoil
    ?? (hasPriceData ? (priceData.usdFoil != null && Number(priceData.usdFoil) > 0) : (card?.foil ?? false));

  const availableInNonFoil = matchingSetDetail?.availableInNonFoil
    ?? (hasPriceData ? (priceData.usd != null && Number(priceData.usd) > 0) : true);

  // Combine physical availability with user preferences
  const canOfferFoil = availableInFoil && acceptFoil;
  const canOfferNonFoil = availableInNonFoil && acceptNonFoil;

  // Default to non-foil when possible; only default to foil when non-foil isn't an option
  let defaultFoil = false;
  if (!canOfferNonFoil && canOfferFoil) {
    defaultFoil = true;
  }

  return { availableInFoil, availableInNonFoil, defaultFoil };
}
